
import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Download, Trash2, Eye, Clock, File, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';
import { StreamRecordingDetails } from '@/components/admin/stream-recording-details';

interface Recording {
  id: string;
  streamId: number;
  title: string;
  duration: number;
  fileSize: number;
  createdAt: string;
  status: 'processing' | 'ready' | 'failed';
  downloadUrl?: string;
}

interface StreamRecordingListProps {
  streamId: number;
}

export function StreamRecordingList({ streamId }: StreamRecordingListProps) {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState<Recording | null>(null);

  const fetchRecordings = async () => {
    setIsLoading(true);
    try {
      const response = await api.get<{ recordings: Recording[] }>(`api/streams/${streamId}/recordings`);

      if (response.success && response.data) {
        setRecordings(response.data.recordings || []);
      } else {
        toast.error(response.error?.message || 'Failed to load recordings');
        console.error('Fetch recordings error:', response.error);
      }
    } catch (error) {
      toast.error('Failed to load recordings');
      console.error('Fetch recordings error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteRecording = async (recordingId: string) => {
    setIsDeleting(recordingId);
    try {
      const response = await api.delete(`api/streams/${streamId}/recordings/${recordingId}`);

      if (response.success) {
        toast.success('Recording deleted successfully');
        fetchRecordings(); // Refresh the list
      } else {
        toast.error(response.error?.message || 'Failed to delete recording');
        console.error('Delete recording error:', response.error);
      }
    } catch (error) {
      toast.error('Failed to delete recording');
      console.error('Delete recording error:', error);
    } finally {
      setIsDeleting(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    fetchRecordings();
    
  }, [streamId]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <File className="h-5 w-5" />
              Recordings
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              View and manage recordings for this stream
            </p>
          </div>
          <Button variant="outline" onClick={fetchRecordings} disabled={isLoading}>
            <Clock className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-12 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : recordings.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
            <File className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No recordings found</p>
            <p className="text-sm mt-2">Recordings will appear here when available</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>File Size</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recordings.map((recording) => (
                <TableRow 
                  key={recording.id}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => {
                    setSelectedRecording(recording);
                    setDetailsDialogOpen(true);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedRecording(recording);
                      setDetailsDialogOpen(true);
                    }
                  }}
                  aria-label={`View details for ${recording.title}`}
                >
                  <TableCell className="font-medium">
                    <button
                      type="button"
                      className="hover:underline focus:outline-none focus:underline text-left"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRecording(recording);
                        setDetailsDialogOpen(true);
                      }}
                    >
                      {recording.title}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      {formatDuration(recording.duration)}
                    </div>
                  </TableCell>
                  <TableCell>{formatFileSize(recording.fileSize)}</TableCell>
                  <TableCell>
                    <Badge variant={
                      recording.status === 'ready' ? 'default' : 
                      recording.status === 'processing' ? 'secondary' : 'destructive'
                    }>
                      {recording.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      {new Date(recording.createdAt).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => window.open(recording.downloadUrl || '#', '_blank')}
                        disabled={!recording.downloadUrl || recording.status !== 'ready'}
                        title="Download Recording"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => deleteRecording(recording.id)}
                        disabled={isDeleting === recording.id}
                        title="Delete Recording"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      
      <CardFooter className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          Showing {recordings.length} recording{recordings.length !== 1 ? 's' : ''}
        </div>
        <Button variant="outline" size="icon" onClick={fetchRecordings}>
          <Eye className="h-4 w-4" />
        </Button>
      </CardFooter>

      {/* Recording Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Recording Details</DialogTitle>
            <DialogDescription>
              View details and download recording
            </DialogDescription>
          </DialogHeader>
          {selectedRecording && (
            <StreamRecordingDetails 
              streamId={streamId} 
              recordingId={selectedRecording.id} 
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
